import { useGlobalImpressionTracking } from '@/contexts/GlobalImpressionContext';
import type { Listing } from '@/services/api';
import { useEffect, useRef } from 'react';

interface UseImpressionTrackingOptions {
    /**
     * The percentage of the element that needs to be visible to trigger an impression
     * Default: 0.5 (50%)
     */
    threshold?: number;
    /**
     * How long the element needs to be visible to count as an impression (in milliseconds)
     * Default: 1000ms (1 second)
     */
    minVisibleTime?: number;
    /**
     * Additional metadata to include with the impression event
     */
    metadata?: Record<string, unknown>;
    /**
     * Whether to enable error logging for this tracking instance
     * Default: true
     */
    enableErrorLogging?: boolean;
}

/**
 * Simplified unified impression tracking hook with error handling
 * Uses the global impression provider for efficient O(1) observer scaling
 * @param listing - The listing to track impressions for
 * @param options - Configuration options for impression tracking
 * @returns A ref to attach to the element you want to track
 */
export function useImpressionTracking(
    listing: Listing | null,
    options: UseImpressionTrackingOptions = {}
) {
    const {
        threshold = 0.5,
        minVisibleTime = 1000,
        metadata = {},
        enableErrorLogging = true
    } = options;

    const { trackElement } = useGlobalImpressionTracking();
    const elementRef = useRef<HTMLDivElement>(null);
    const cleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!listing || !elementRef.current) {
            return;
        }

        try {
            cleanupRef.current = trackElement(elementRef.current, listing, {
                threshold,
                minVisibleTime,
                metadata,
            });

            return () => {
                if (cleanupRef.current) {
                    try {
                        cleanupRef.current();
                    } catch (error) {
                        if (enableErrorLogging) {
                            console.warn('Error during impression tracking cleanup:', error);
                        }
                    }
                    cleanupRef.current = null;
                }
            };
        } catch (error) {
            if (enableErrorLogging) {
                console.warn('Error setting up impression tracking:', {
                    error,
                    listingId: listing._id,
                    metadata,
                });
            }
            return;
        }
    }, [listing, threshold, minVisibleTime, trackElement, metadata, enableErrorLogging]);

    return elementRef;
}
